import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Bell,
  Mail,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Power,
  UserCheck,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Dialog, cn } from '../../ui';
import Paginacion from '../components/Paginacion';
import StockAlertForm from '../components/StockAlertForm';
import NotifRuleForm from '../components/NotifRuleForm';
import {
  useStockAlerts,
  useNotifRules,
  useNotifLog,
} from '../hooks/useAlertasCrud';
import { useAssetTypes } from '../hooks/useAssetTypes';
import { getIcon } from '../icons';
import { etiquetaAmbitoRegla } from '../utils/ambitoRegla';
import { api } from '../api';
import GuiaRapida from '../components/GuiaRapida';
import HelpTip from '../components/HelpTip';

/**
 * Ajustes (III.3) — pantalla de configuración del subsistema de avisos.
 *
 * Tres pestañas, cada una sobre un recurso del backend II.5a:
 *   1. Umbrales de stock   (StockAlert       CRUD)
 *   2. Reglas de email     (NotificationRule CRUD)
 *   3. Historial de envíos (NotificationLog  read-only, paginado)
 *
 * Endpoints reales bajo /api/inventario/alertas/* — no hay mock.
 *
 * Reglas visuales (INSTRUCCIONES_PROYECTO):
 *   - contenedor px-6 py-5, sin min-h-screen ni max-w
 *   - cards con CardContent p-4, gap-3 entre tarjetas
 *   - Dialog del DS con prop `footer` (esta pantalla NO va dentro de
 *     un Drawer, así que el footer sí se pinta — patrón usado también
 *     en TiposList; el truco de 6b-2 era específico de AccionesActivo
 *     dentro del Drawer).
 */

const TABS = [
  { key: 'umbrales', label: 'Umbrales de stock', Icon: Bell },
  { key: 'reglas', label: 'Reglas de notificación', Icon: Mail },
  { key: 'historial', label: 'Historial de envíos', Icon: Activity },
];

const TRIGGER_LABEL = {
  asignacion: 'Asignación',
  devolucion: 'Devolución',
  baja: 'Baja',
  reparacion_inicio: 'Entrada a reparación',
  reparacion_fin: 'Fin de reparación',
  stock_bajo: 'Stock bajo',
};


export default function Ajustes() {
  const [tab, setTab] = useState('umbrales');

  // Catálogo de tipos (una sola carga compartida por Umbrales y Reglas).
  // Lo usamos para resolver slug → { nombre, icon } en la columna TIPO. Indexado por
  // `slug|scope` (el slug se repite entre ámbitos) y, de respaldo, por slug a secas
  // (el primero del catálogo: para las reglas de «los dos ámbitos»).
  const { tipos } = useAssetTypes();
  const tiposPorSlug = useMemo(() => {
    const m = new Map();
    (tipos || []).forEach((t) => {
      m.set(`${t.slug}|${t.scope}`, t);
      if (!m.has(t.slug)) m.set(t.slug, t);
    });
    return m;
  }, [tipos]);

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
        <h1 className="text-xl font-semibold text-slate-900 leading-tight">
          Ajustes de avisos
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Umbrales de stock, reglas de notificación por email e historial
          de envíos · estos ajustes alimentan las alertas que ve el
          Dashboard.
        </p>
        </div>
        <GuiaRapida seccion="ajustes" />
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors',
                active
                  ? 'border-[hsl(var(--brand-light))] text-[hsl(var(--brand-light))] font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'umbrales' && <SeccionUmbrales />}
      {tab === 'reglas' && <SeccionReglas tiposPorSlug={tiposPorSlug} />}
      {tab === 'historial' && <SeccionHistorial />}
    </div>
  );
}


// ====================================================================
// 1 · Umbrales de stock
// ====================================================================

function SeccionUmbrales() {
  const { items, loading, error, create, update, remove } = useStockAlerts();
  const [modal, setModal] = useState(null); // { mode: 'crear'|'editar', row? }
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  async function handleSubmit(payload) {
    if (modal.mode === 'crear') await create(payload);
    else await update(modal.row.id, payload);
    setModal(null);
  }

  async function confirmDelete() {
    setDeleteBusy(true);
    try {
      await remove(deleting.id);
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-end mb-3">
        <Button onClick={() => setModal({ mode: 'crear' })}>
          <Plus className="w-4 h-4 mr-1" />
          Nuevo umbral
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando umbrales…
            </div>
          )}
          {error && (
            <div className="p-4 text-sm text-rose-600">
              Error al cargar: {error?.response?.data?.error || error.message}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <EmptyState
              icon={Bell}
              title="Aún no hay umbrales configurados"
              cta="Crear el primero"
              onCta={() => setModal({ mode: 'crear' })}
            />
          )}
          {!loading && !error && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Empresa</th>
                  <th className="text-right px-4 py-2 font-medium">Mínimo</th>
                  <th className="text-left px-4 py-2 font-medium">Severidad</th>
                  <th className="text-left px-4 py-2 font-medium">Estado</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <CeldaTipoAmbito row={r} />
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.empresa_id == null ? (
                        <span className="text-slate-400 italic">
                          Todas las empresas
                        </span>
                      ) : r.empresa_rota ? (
                        <span className="text-amber-600">{r.empresa_nombre}</span>
                      ) : (
                        r.empresa_nombre
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {r.stock_minimo}
                    </td>
                    <td className="px-4 py-2">
                      <SeveridadBadge severidad={r.severidad} />
                    </td>
                    <td className="px-4 py-2">
                      {r.is_active ? (
                        <Badge variant="success">Activo</Badge>
                      ) : (
                        <Badge variant="secondary">Inactivo</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <RowActions
                        onEdit={() => setModal({ mode: 'editar', row: r })}
                        onDelete={() => setDeleting(r)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!modal}
        onClose={() => setModal(null)}
        title={
          modal?.mode === 'crear' ? 'Nuevo umbral de stock' : 'Editar umbral'
        }
        description="Cuando el stock disponible para esta combinación caiga al o por debajo del mínimo, el Dashboard mostrará una alerta."
      >
        {modal && (
          <StockAlertForm
            initial={modal.row || null}
            onSubmit={handleSubmit}
            onCancel={() => setModal(null)}
          />
        )}
      </Dialog>

      <Dialog
        open={!!deleting}
        onClose={() => !deleteBusy && setDeleting(null)}
        title="Eliminar umbral"
        description={
          deleting
            ? `Se eliminará el umbral de "${deleting.tipo_nombre || deleting.asset_type_slug}` +
              `${deleting.scope ? ` · ${deleting.scope === 'colab' ? 'colab' : 'GRUPOLAE'}` : ''}"` +
              (deleting.empresa_id == null
                ? ' (todas las empresas).'
                : ` para la empresa ${deleting.empresa_nombre || '#' + deleting.empresa_id}.`)
            : ''
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleteBusy}
            >
              {deleteBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Eliminar
            </Button>
          </div>
        }
      />
    </>
  );
}


// ====================================================================
// 2 · Reglas de notificación
// ====================================================================

function SeccionReglas({ tiposPorSlug }) {
  const { items, loading, error, create, update, remove } = useNotifRules();
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  async function handleSubmit(payload) {
    if (modal.mode === 'crear') await create(payload);
    else await update(modal.row.id, payload);
    setModal(null);
  }

  async function confirmDelete() {
    setDeleteBusy(true);
    try {
      await remove(deleting.id);
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <NotifAutomaticasPanel />

      <div className="flex items-center justify-end mb-3">
        <Button onClick={() => setModal({ mode: 'crear' })}>
          <Plus className="w-4 h-4 mr-1" />
          Nueva regla
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando reglas…
            </div>
          )}
          {error && (
            <div className="p-4 text-sm text-rose-600">
              Error al cargar: {error?.response?.data?.error || error.message}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <EmptyState
              icon={Mail}
              title="Aún no hay reglas de notificación"
              cta="Crear la primera"
              onCta={() => setModal({ mode: 'crear' })}
            />
          )}
          {!loading && !error && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Ámbito</th>
                  <th className="text-left px-4 py-2 font-medium">Nombre</th>
                  <th className="text-left px-4 py-2 font-medium">Disparador</th>
                  <th className="text-left px-4 py-2 font-medium">
                    Destinatarios
                  </th>
                  <th className="text-left px-4 py-2 font-medium">Estado</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      {r.asset_type_slug ? (
                        <CeldaTipo slug={r.asset_type_slug} scope={r.scope} tiposPorSlug={tiposPorSlug} />
                      ) : (
                        <span className="text-slate-400 italic">Todos</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {r.asset_type_slug ? (
                        <Badge variant={r.scope === 'colab' ? 'purple' : r.scope === 'interno' ? 'info' : 'secondary'}>
                          {etiquetaAmbitoRegla(r)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 italic">{etiquetaAmbitoRegla(r)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-900">
                      {r.nombre}
                    </td>
                    <td className="px-4 py-2">
                      {r.trigger_kind === 'fecha' ? (
                        <Badge variant="purple">
                          Fecha: {r.date_field} · {r.days_before}d
                        </Badge>
                      ) : (
                        <Badge variant="info">
                          {TRIGGER_LABEL[r.trigger_event] || r.trigger_event}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {(() => {
                        const nIds = String(r.recipient_person_ids || '')
                          .split(',').filter(Boolean).length;
                        const nColab = String(r.recipient_colab_ids || '')
                          .split(',').filter(Boolean).length;
                        const tieneAlgo = !!r.recipients || nIds > 0 || nColab > 0 || !!r.notify_assignee;
                        return (
                          <div className="flex flex-wrap items-center gap-1">
                            {r.recipients ? (
                              <span className="text-slate-700 text-xs font-mono max-w-[260px] truncate">
                                {r.recipients}
                              </span>
                            ) : null}
                            {nIds > 0 && (
                              <Badge variant="secondary">
                                {nIds} usuario{nIds > 1 ? 's' : ''}
                              </Badge>
                            )}
                            {nColab > 0 && (
                              <Badge variant="purple">
                                {nColab} colaborador{nColab > 1 ? 'es' : ''}
                              </Badge>
                            )}
                            {r.notify_assignee && (
                              <Badge variant="info" className="inline-flex items-center gap-1">
                                <UserCheck className="w-3 h-3" /> Usuario asignado
                              </Badge>
                            )}
                            {!tieneAlgo && <span className="text-slate-400">—</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2">
                      {r.is_active ? (
                        <Badge variant="success">Activa</Badge>
                      ) : (
                        <Badge variant="secondary">Inactiva</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <RowActions
                        onEdit={() => setModal({ mode: 'editar', row: r })}
                        onDelete={() => setDeleting(r)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === 'crear' ? 'Nueva regla' : 'Editar regla'}
        description="Cuando ocurra el evento elegido, el motor de notificaciones enviará un email a los destinatarios."
      >
        {modal && (
          <NotifRuleForm
            initial={modal.row || null}
            onSubmit={handleSubmit}
            onCancel={() => setModal(null)}
          />
        )}
      </Dialog>

      <Dialog
        open={!!deleting}
        onClose={() => !deleteBusy && setDeleting(null)}
        title="Eliminar regla"
        description={
          deleting
            ? `Se eliminará la regla "${deleting.nombre}".`
            : ''
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleteBusy}
            >
              {deleteBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Eliminar
            </Button>
          </div>
        }
      />
    </>
  );
}


// ====================================================================
// 3 · Historial de envíos (read-only paginado)
// ====================================================================

function SeccionHistorial() {
  const [status, setStatus] = useState('');
  const { items, pagination, page, setPage, loading, error } = useNotifLog({
    status: status || undefined,
    pageSize: 25,
  });

  return (
    <>
      <div className="flex items-center gap-2 mb-3 text-xs">
        <span className="text-slate-500">Filtrar por estado:</span>
        {[
          { value: '', label: 'Todos' },
          { value: 'sent', label: 'Enviados' },
          { value: 'failed', label: 'Fallidos' },
          { value: 'pending', label: 'Pendientes' },
        ].map((opt) => (
          <button
            key={opt.value || 'all'}
            onClick={() => setStatus(opt.value)}
            className={cn(
              'px-2.5 py-1 rounded-full border transition-colors',
              status === opt.value
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando historial…
            </div>
          )}
          {error && (
            <div className="p-4 text-sm text-rose-600">
              Error al cargar: {error?.response?.data?.error || error.message}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <EmptyState
              icon={Activity}
              title="Sin envíos registrados"
              hint="El historial se llenará cuando el motor de notificaciones envíe emails."
            />
          )}
          {!loading && !error && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Fecha</th>
                  <th className="text-left px-4 py-2 font-medium">Regla</th>
                  <th className="text-left px-4 py-2 font-medium">Ámbito</th>
                  <th className="text-left px-4 py-2 font-medium">
                    Destinatarios
                  </th>
                  <th className="text-left px-4 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-xs text-slate-500 font-mono whitespace-nowrap">
                      {formatDateTime(r.sent_at || r.created_at)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm text-slate-900">
                        {r.rule?.nombre || (
                          <span className="text-slate-400 italic">
                            (regla eliminada)
                          </span>
                        )}
                      </div>
                      {r.rule?.trigger_event && (
                        <div className="text-[11px] text-slate-400">
                          {TRIGGER_LABEL[r.rule.trigger_event] ||
                            r.rule.trigger_event}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {/* Ámbito del ACTIVO avisado (Fase 4). Los envíos de antes no lo guardaban. */}
                      {r.scope === 'colab' ? 'Colaboradores'
                        : r.scope === 'interno' ? 'GRUPOLAE'
                          : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 font-mono max-w-[260px] truncate">
                      {r.recipients}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.status} />
                      {r.status === 'failed' && r.error_message && (
                        <div className="text-[11px] text-rose-500 mt-0.5 max-w-[260px] truncate">
                          {r.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {!loading && !error && pagination.total > 0 && (
        <Paginacion
          page={page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={setPage}
        />
      )}
    </>
  );
}


// ====================================================================
// Componentes auxiliares locales
// ====================================================================

/**
 * Celda "Tipo" de la tabla de REGLAS: icono lucide (mismo map que el sidebar via
 * getIcon) + nombre legible del AssetType. El ámbito de la regla va en su propia
 * columna (Fase 4). Cae al slug si el catálogo aún no cargó o el slug no está
 * (defensivo).
 */
function CeldaTipo({ slug, scope, tiposPorSlug }) {
  const get = (k) => (tiposPorSlug && tiposPorSlug.get ? tiposPorSlug.get(k) : null);
  const t = (scope && get(`${slug}|${scope}`)) || get(slug);
  const nombre = (t && t.nombre) || slug;
  const Icono = getIcon(t && t.icon);
  return (
    <span className="inline-flex items-center gap-2">
      <Icono className="w-4 h-4 text-[hsl(var(--brand-light))]" />
      <span className="text-slate-700">{nombre}</span>
    </span>
  );
}

/**
 * Celda de tipo para la tabla de UMBRALES. A diferencia de CeldaTipo (resuelve
 * por slug, ambiguo entre ámbitos), pinta directamente desde la fila del umbral
 * —que ya trae `tipo` (nombre, scope, icon) por asset_type_id— y muestra el
 * ámbito. Así "Móvil · colab" y "Móvil · GRUPOLAE" se distinguen en el listado.
 */
function CeldaTipoAmbito({ row }) {
  const t = row && row.tipo ? row.tipo : null;
  const nombre = row.tipo_nombre || (t && t.nombre) || row.asset_type_slug;
  const scope = row.scope || (t && t.scope) || null;
  const ambito = scope ? (scope === 'colab' ? 'colab' : 'GRUPOLAE') : null;
  const Icono = getIcon(t && t.icon);
  return (
    <span className="inline-flex items-center gap-2">
      <Icono className="w-4 h-4 text-[hsl(var(--brand-light))]" />
      <span className="text-slate-700">
        {nombre}
        {ambito && <span className="text-slate-400"> · {ambito}</span>}
      </span>
    </span>
  );
}

function RowActions({ onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={onEdit}
        className="flex items-center gap-1 text-xs text-slate-600 hover:text-[hsl(var(--brand-light))] px-2 py-1 rounded hover:bg-slate-50"
      >
        <Pencil className="w-3.5 h-3.5" />
        Editar
      </button>
      <button
        onClick={onDelete}
        className="flex items-center gap-1 text-xs text-slate-600 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50"
      >
        <Trash2 className="w-3.5 h-3.5" />
        Eliminar
      </button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint, cta, onCta }) {
  return (
    <div className="p-10 text-center">
      <Icon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-600">{title}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      {cta && (
        <Button variant="secondary" className="mt-3" onClick={onCta}>
          <Plus className="w-4 h-4 mr-1" />
          {cta}
        </Button>
      )}
    </div>
  );
}

function SeveridadBadge({ severidad }) {
  const cfg = {
    info: { variant: 'info', label: 'info' },
    warning: { variant: 'warning', label: 'warning' },
    danger: { variant: 'danger', label: 'danger' },
  }[severidad] || { variant: 'secondary', label: severidad || '—' };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

function StatusBadge({ status }) {
  if (status === 'sent') {
    return (
      <Badge variant="success">
        <CheckCircle2 className="w-3 h-3 mr-1 inline" />
        Enviado
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="danger">
        <AlertTriangle className="w-3 h-3 mr-1 inline" />
        Fallido
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge variant="warning">
        <Clock className="w-3 h-3 mr-1 inline" />
        Pendiente
      </Badge>
    );
  }
  return <Badge variant="secondary">{status || '—'}</Badge>;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}


// ====================================================================
// Notificaciones automáticas (Pieza 3) · toggles por evento
// ====================================================================

const AUTO_EVENTOS = [
  { id: 'asignacion', label: 'Asignación' },
  { id: 'devolucion', label: 'Devolución' },
  { id: 'baja', label: 'Baja' },
];

function NotifAutomaticasPanel() {
  const [cfg, setCfg] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingEv, setSavingEv] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/inapp-auto');
        if (alive) setCfg(res?.data?.data || res?.data || {});
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function toggle(evento, activo) {
    setSavingEv(evento);
    setError(null);
    try {
      const res = await api.put(`/inapp-auto/${evento}`, { activo });
      const row = res?.data?.data || res?.data || { evento, activo };
      setCfg((prev) => ({ ...prev, [evento]: { ...(prev[evento] || {}), ...row, activo: !!row.activo } }));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSavingEv(null);
    }
  }

  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Notificaciones automáticas en la app (campana)
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Avisa al usuario asignado del activo, sin necesidad de regla, cuando ocurren estos eventos.
            </p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {AUTO_EVENTOS.map((ev) => {
              const checked = !!cfg[ev.id]?.activo;
              const saving = savingEv === ev.id;
              return (
                <label key={ev.id} className="flex items-center justify-between py-2 cursor-pointer">
                  <span className="text-sm text-slate-700">
                    Avisar al usuario asignado en la app · <strong>{ev.label}</strong>
                  </span>
                  <span className="flex items-center gap-2">
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={(e) => toggle(ev.id, e.target.checked)}
                    />
                  </span>
                </label>
              );
            })}
            {(() => {
              const vencActivo = !!cfg.vencimiento?.activo;
              const vencDias = cfg.vencimiento?.dias ?? 15;
              const saving = savingEv === 'vencimiento';
              return (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-700">
                    Avisar al usuario asignado en la app · <strong>Vencimiento (ITV…)</strong>
                  </span>
                  <div className="flex items-center gap-3">
                    {vencActivo && (
                      <label className="text-xs text-slate-500 flex items-center gap-1">
                        Horizonte
                        <input
                          type="number"
                          min={0}
                          className="w-16 border border-slate-200 rounded-md px-2 py-1 text-xs"
                          value={vencDias}
                          onChange={(e) =>
                            setCfg((c) => ({
                              ...c,
                              vencimiento: { ...(c.vencimiento || {}), dias: e.target.value },
                            }))
                          }
                          onBlur={async (e) => {
                            const n = Number(e.target.value);
                            if (!Number.isInteger(n) || n < 0) return;
                            try {
                              setSavingEv('vencimiento');
                              const res = await api.put('/inapp-auto/vencimiento', { dias: n });
                              const row = res?.data?.data || res?.data || { dias: n };
                              setCfg((c) => ({
                                ...c,
                                vencimiento: { ...(c.vencimiento || {}), ...row },
                              }));
                            } catch (err) {
                              setError(err?.response?.data?.error || err.message);
                            } finally {
                              setSavingEv(null);
                            }
                          }}
                        />
                        días
                      </label>
                    )}
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                    <input
                      type="checkbox"
                      checked={vencActivo}
                      disabled={saving}
                      onChange={(e) => toggle('vencimiento', e.target.checked)}
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {error && (
          <p className="text-xs text-rose-600 mt-2">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}
